import React, { useState, useEffect, useCallback } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { useWebsiteSettings } from '@/hooks/useWebsiteSettings';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';

// Import tab components
import GeneralSettingsTab from '@/components/admin/settings/GeneralSettingsTab';
import AppearanceSettingsTab from '@/components/admin/settings/AppearanceSettingsTab';
import AdsSettingsTab from '@/components/admin/settings/AdsSettingsTab';
import SEOSettingsTab from '@/components/admin/settings/SEOSettingsTab';
import SecuritySettingsTab from '@/components/admin/settings/SecuritySettingsTab';
import GDPRSettingsTab from '@/components/admin/settings/GDPRSettingsTab';
import AdvancedSettingsTab from '@/components/admin/settings/AdvancedSettingsTab';
import APISettingsTab from '@/components/admin/settings/APISettingsTab';

// Import types
import type { WebsiteSettings, GdprSettings } from '@/components/admin/settings/types';

const AdminSettings = () => {
  const [settings, setSettings] = useState<WebsiteSettings>({
    site_name: 'TerraGamers',
    site_description: '',
    site_url: '',
    admin_email: '',
    logo_url: '',
    favicon_url: '',
    logo_mobile_height: 48,
    logo_desktop_height: 64,
    logo_max_width: 200,
    logo_max_width_desktop: 280,
    maintenance_mode: false,
    allow_registration: false,
    google_analytics_id: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    native_ad_frequency: 8,
    footer_text: '',
    discord_url: '',
    twitter_url: '',
    youtube_url: '',
    github_url: '',
    donate_url: '',
    coffee_url: '',
    donate_text: '',
    coffee_text: '',
    maintenance_mode_message: "We are currently performing scheduled maintenance. Please check back soon.",
    auto_block_admin_access: true,
  });

  const [gdprSettings, setGdprSettings] = useState<GdprSettings>({
    gdpr_enabled: true,
    consent_banner_text: '',
    privacy_policy_url: '',
    cookie_policy_url: '',
    consent_mode_enabled: true,
    data_retention_days: 730,
    consent_categories: {},
  });

  const queryClient = useQueryClient();

  // Use the simplified settings hook
  const { 
    settings: fetchedSettings, 
    isLoading: isLoadingSettings, 
    error: settingsError, 
    refetch: refetchSettings,
    updateSettings,
    isUpdating
  } = useWebsiteSettings();

  // Simplified GDPR settings fetch
  const { data: fetchedGdprSettings, isLoading: isLoadingGdpr, error: gdprError, refetch: refetchGdpr } = useQuery({
    queryKey: ['gdpr-settings'],
    queryFn: async () => {
      console.log('Fetching GDPR settings...');
      const { data, error } = await supabase
        .from('gdpr_settings')
        .select('key, value');
      
      if (error) {
        console.error('GDPR settings error:', error);
        throw error;
      }
      
      const settingsObj = (data || []).reduce((acc: Record<string, any>, setting) => {
        acc[setting.key] = setting.value;
        return acc;
      }, {});
      
      console.log('GDPR settings fetched:', settingsObj);
      return settingsObj;
    },
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: 2,
  });

  // Fetch user consents for analytics
  const { data: userConsents } = useQuery({
    queryKey: ['user-consents'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_consents')
        .select('*')
        .order('consent_timestamp', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      return data;
    },
    retry: 2,
  });

  // Simplified GDPR settings mutation
  const updateGdprSettingsMutation = useMutation({
    mutationFn: async (settingsToUpdate: Record<string, any>) => {
      console.log('Updating GDPR settings:', settingsToUpdate);
      
      const updatePromises = Object.entries(settingsToUpdate).map(async ([key, value]) => {
        const jsonValue: Json = typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null
          ? value
          : JSON.stringify(value);
          
        const { error } = await supabase
          .from('gdpr_settings')
          .upsert({ 
            key, 
            value: jsonValue, 
            description: `GDPR setting for ${key}` 
          }, {
            onConflict: 'key'
          });
          
        if (error) {
          throw error;
        }
      });
      
      await Promise.all(updatePromises);
    },
    onSuccess: async () => {
      console.log('GDPR settings updated successfully');
      await queryClient.invalidateQueries({ queryKey: ['gdpr-settings'] });
      await queryClient.refetchQueries({ queryKey: ['gdpr-settings'] });
      toast.success('GDPR settings updated successfully');
    },
    onError: (error: any) => {
      console.error('GDPR settings update error:', error);
      toast.error('Failed to update GDPR settings. Please try again.');
    },
  });

  // Delete old consents mutation
  const deleteOldConsentsMutation = useMutation({
    mutationFn: async () => {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - (gdprSettings.data_retention_days || 730));
      
      const { error } = await supabase
        .from('user_consents')
        .delete()
        .lt('consent_timestamp', cutoffDate.toISOString());
        
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['user-consents'] });
      await queryClient.refetchQueries({ queryKey: ['user-consents'] });
      toast.success('Old consent records deleted successfully');
    },
    onError: (error: any) => {
      console.error('Delete consents error:', error);
      toast.error('Failed to delete old consent records');
    },
  });

  // Initialize settings when data is available
  useEffect(() => {
    if (fetchedSettings) {
      console.log('Initializing settings from fetched data:', fetchedSettings);
      setSettings(prev => {
        const newSettings = { 
          ...prev, 
          ...fetchedSettings,
          site_name: fetchedSettings.site_name || 'TerraGamers',
        };
        console.log('Settings initialized:', newSettings);
        return newSettings;
      });
    }
  }, [fetchedSettings]);

  useEffect(() => {
    if (fetchedGdprSettings) {
      console.log('Initializing GDPR settings:', fetchedGdprSettings);
      setGdprSettings(prev => ({ ...prev, ...fetchedGdprSettings }));
    }
  }, [fetchedGdprSettings]);

  const handleInputChange = useCallback((key: string, value: any) => {
    console.log('Setting input change:', key, value);
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleGdprChange = useCallback((key: string, value: any) => {
    console.log('GDPR setting change:', key, value);
    setGdprSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleSaveGdprSettings = useCallback(() => {
    console.log('Saving GDPR settings:', gdprSettings);
    updateGdprSettingsMutation.mutate(gdprSettings);
  }, [gdprSettings, updateGdprSettingsMutation]);

  const handleManualRefresh = useCallback(async () => {
    console.log('Manual refresh triggered');
    try {
      await refetchSettings();
      await refetchGdpr();
      toast.info('Settings refreshed');
    } catch (error) {
      console.error('Refresh error:', error);
      toast.error('Failed to refresh settings');
    }
  }, [refetchSettings, refetchGdpr]);

  // Error handling
  if (settingsError || gdprError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-red-600 text-lg font-semibold">Unable to Load Settings</div>
        <div className="text-gray-600 text-center max-w-md">
          {settingsError?.message || gdprError?.message || 'Settings could not be loaded'}
        </div>
        <Button onClick={handleManualRefresh} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  // Loading state
  if (isLoadingSettings || isLoadingGdpr) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <div className="text-gray-600">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Website Settings</h1>
          <p className="text-gray-600">Configure your website settings and preferences</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleManualRefresh} variant="outline" size="sm" disabled={isUpdating}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Settings
          </Button>
          {isUpdating && (
            <div className="flex items-center text-sm text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              Saving...
            </div>
          )}
        </div>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 lg:grid-cols-8">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="appearance">Appearance</TabsTrigger>
          <TabsTrigger value="api">API</TabsTrigger>
          <TabsTrigger value="ads">Ads</TabsTrigger>
          <TabsTrigger value="seo">SEO</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          <TabsTrigger value="gdpr">GDPR</TabsTrigger>
          <TabsTrigger value="advanced">Advanced</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <GeneralSettingsTab 
            settings={settings} 
            onSettingsChange={handleInputChange} 
          />
        </TabsContent>

        <TabsContent value="appearance">
          <AppearanceSettingsTab 
            settings={settings} 
            onSettingsChange={handleInputChange} 
          />
        </TabsContent>

        <TabsContent value="api">
          <APISettingsTab />
        </TabsContent>

        <TabsContent value="ads">
          <AdsSettingsTab 
            settings={settings} 
            onSettingsChange={handleInputChange} 
          />
        </TabsContent>

        <TabsContent value="seo">
          <SEOSettingsTab 
            settings={settings} 
            onSettingsChange={handleInputChange} 
          />
        </TabsContent>

        <TabsContent value="security">
          <SecuritySettingsTab 
            settings={settings} 
            onSettingsChange={handleInputChange} 
          />
        </TabsContent>

        <TabsContent value="gdpr">
          <GDPRSettingsTab 
            gdprSettings={gdprSettings}
            onGdprChange={handleGdprChange}
            onSaveGdprSettings={handleSaveGdprSettings}
            updateGdprSettingsMutation={updateGdprSettingsMutation}
            userConsents={userConsents}
            deleteOldConsentsMutation={deleteOldConsentsMutation}
          />
        </TabsContent>

        <TabsContent value="advanced">
          <AdvancedSettingsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSettings;
